import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { ClockCounterClockwise, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const SOURCE_STYLE = {
  sto_relocation: { label: "STO Relocation", cls: "bg-[#EFF4FF] text-[#3E4784]" },
  store_issue: { label: "Store Issue", cls: "bg-[#F0F9FA] text-[#0B6B74]" },
  store_return: { label: "Store Return", cls: "bg-[#FDF2FA] text-[#9E165F]" },
};

// Movement Audit Trail (Sep 24 2026, user's explicit ask) - read-only
// cross-cutting ledger merging every real SAP Goods Movement attempt
// across the 3 places this app posts one: STO Relocation, Store Issue,
// Store Return. All 3 already share the same atomic-batch client method
// and the same culprit/"blocked because bundled with X" convention on a
// rejected batch - `is_culprit` here just surfaces that distinction so a
// batch failure's real cause is obvious at a glance, not just "everyone
// in this batch failed".
export default function MovementAuditTrailPage() {
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/movement-audit/sites`).then((res) => setSites(res.data.sites || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (siteFilter !== "all") params.site_id = siteFilter;
    if (sourceFilter !== "all") params.source = sourceFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (search.trim()) params.search = search.trim();
    axios.get(`${API}/movement-audit/list`, { params })
      .then((res) => setRows(res.data.movements || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [siteFilter, sourceFilter, statusFilter, dateFrom, dateTo, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <NavTabs />
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ClockCounterClockwise size={26} weight="bold" className="text-[#3E4784]" />
            <div>
              <h1 className="text-2xl font-semibold text-[#101828]" data-testid="movement-audit-title">Movement Audit Trail</h1>
              <p className="text-sm text-[#667085]">Every SAP Goods Movement across STO Relocation, Store Issue, and Store Return</p>
            </div>
          </div>
          <SapConnectionStatus />
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-4 bg-white border border-[#E4E7EC] rounded-xl p-4">
          <div>
            <label className="text-xs text-[#667085] mb-1 block">Site</label>
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className="w-[140px]" data-testid="movement-audit-site-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {sites.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#667085] mb-1 block">Source</label>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[180px]" data-testid="movement-audit-source-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="sto_relocation">STO Relocation</SelectItem>
                <SelectItem value="store_issue">Store Issue</SelectItem>
                <SelectItem value="store_return">Store Return</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#667085] mb-1 block">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]" data-testid="movement-audit-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#667085] mb-1 block">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px]" data-testid="movement-audit-date-from" />
          </div>
          <div>
            <label className="text-xs text-[#667085] mb-1 block">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px]" data-testid="movement-audit-date-to" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-[#667085] mb-1 block">Search (Reference / Product / GM ID / Person)</label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. STO-000145, G12LW, 283220, Ankit" data-testid="movement-audit-search" />
          </div>
        </div>

        <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Site</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>From → To</TableHead>
                <TableHead>Requested By</TableHead>
                <TableHead>Received / Issued By</TableHead>
                <TableHead>Result</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center text-[#667085] py-8" data-testid="movement-audit-loading">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center text-[#667085] py-8" data-testid="movement-audit-empty">No movements found for this filter.</TableCell></TableRow>
              ) : (
                rows.map((r, i) => {
                  const src = SOURCE_STYLE[r.source] || { label: r.source_label, cls: "bg-[#F2F4F7] text-[#344054]" };
                  return (
                    <TableRow key={`${r.source}-${r.reference_id}-${r.product_id}-${i}`} data-testid={`movement-audit-row-${i}`}>
                      <TableCell className="text-sm text-[#344054] whitespace-nowrap">{formatDate(r.when)}</TableCell>
                      <TableCell><span className={`px-2 py-1 rounded-full text-xs font-medium ${src.cls}`}>{src.label}</span></TableCell>
                      <TableCell className="text-sm font-medium text-[#101828]">{r.reference_id}</TableCell>
                      <TableCell className="text-sm text-[#344054]">{r.site_id || "—"}</TableCell>
                      <TableCell className="text-sm text-[#344054]">
                        {r.product_id}
                        {r.description && <div className="text-xs text-[#98A2B3]">{r.description}</div>}
                        {r.reason_label && <div className="text-xs text-[#B93815]">{r.reason_label}</div>}
                      </TableCell>
                      <TableCell className="text-sm text-[#344054]">{r.quantity ?? "—"} {r.unit_of_measure || ""}</TableCell>
                      <TableCell className="text-xs text-[#667085]">{r.from_warehouse || "—"} → {r.to_warehouse || "—"}</TableCell>
                      <TableCell className="text-sm text-[#344054]" data-testid={`movement-audit-requested-by-${i}`}>
                        {r.requested_by || "—"}
                        {r.requested_by && r.requested_by_role && <div className="text-xs text-[#98A2B3]">{r.requested_by_role}</div>}
                      </TableCell>
                      <TableCell className="text-sm text-[#344054]" data-testid={`movement-audit-received-by-${i}`}>
                        {r.received_by || "—"}
                        {r.received_by && r.received_by_role && <div className="text-xs text-[#98A2B3]">{r.received_by_role}</div>}
                      </TableCell>
                      <TableCell>
                        {r.status === "success" ? (
                          <div className="flex items-center gap-1 text-[#027A48] text-sm" data-testid={`movement-audit-result-success-${i}`}>
                            <CheckCircle size={15} weight="fill" /> GM {r.external_id}
                          </div>
                        ) : (
                          <div className="flex items-start gap-1 text-[#B42318] text-sm max-w-[280px]" data-testid={`movement-audit-result-failed-${i}`}>
                            <WarningCircle size={15} weight="fill" className="mt-0.5 shrink-0" />
                            <span>
                              {r.is_culprit === false && <span className="font-medium">Blocked by batch: </span>}
                              {r.error || "Failed"}
                            </span>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
